// src/hooks/useFileOperations.js - Updated with download progress tracking
import { useState, useCallback } from 'react';
import { useFileContext } from '../contexts/FileContext';
import * as fileService from '../services/file.service';

const useFileOperations = () => {
  const [fileToView, setFileToView] = useState(null);
  const [error, setError] = useState(null);
  const [activeOperations, setActiveOperations] = useState({
    upload: false,
    download: false,
    delete: false,
    view: false
  });

  // Add download progress state
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [showDownloadProgress, setShowDownloadProgress] = useState(false);

  // Destructure values from context with default values for safety
  const fileContext = useFileContext() || {};
  const {
    setUploadProgress = () => { },
    setUploadMessage = () => { },
    setIsUploadInProgress = () => { }
  } = fileContext;

  // Create abort controllers for cancellable operations
  const abortControllers = {
    upload: new AbortController(),
    download: new AbortController()
  };

  // Clear error message
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Progress callback for operations
  const progressCallback = useCallback((operationType, progress, message) => {
    if (operationType === 'upload') {
      setUploadProgress(progress);
      setUploadMessage(message);
    } else if (operationType === 'download') {
      setDownloadProgress(progress);
      setDownloadMessage(message);
    }
  }, [setUploadProgress, setUploadMessage]);

  // SIMPLIFIED: Upload files - now just a wrapper for backward compatibility
  // Most uploads should go through the queue system instead
  const uploadFiles = useCallback(async (files, folderPath, uploadType = 'files') => {
    console.warn('useFileOperations.uploadFiles is deprecated. Use UploadQueue.addToQueue instead.');
    
    if (!files || files.length === 0) {
      setError('No files selected for upload');
      return false;
    }

    try {
      setActiveOperations(prev => ({ ...prev, upload: true }));
      setIsUploadInProgress(true);
      setUploadProgress(0);
      setUploadMessage('Starting upload...');

      // Use the fixed uploadFiles service function
      const result = await fileService.uploadFiles(
        files, 
        folderPath, 
        (progress, message) => {
          setUploadProgress(progress);
          setUploadMessage(message);
        }, 
        abortControllers.upload
      );

      if (result.success) {
        setUploadProgress(100);
        setUploadMessage(result.message || 'Upload completed');
        
        setTimeout(() => {
          setIsUploadInProgress(false);
          setActiveOperations(prev => ({ ...prev, upload: false }));
        }, 2000);
        
        return true;
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.message === 'Upload cancelled') {
        setUploadMessage('Upload cancelled');
      } else {
        console.error('Error uploading files:', err);
        setError(`Failed to upload: ${err.message}`);
        setUploadMessage(`Failed: ${err.message}`);
      }

      setUploadProgress(100);
      
      setTimeout(() => {
        setIsUploadInProgress(false);
        setActiveOperations(prev => ({ ...prev, upload: false }));
      }, 2000);

      return false;
    } finally {
      // Reset abort controller
      abortControllers.upload = new AbortController();
    }
  }, [setIsUploadInProgress, setUploadProgress, setUploadMessage]);

  // Handle file download - Updated with progress modal
const downloadFile = useCallback(async (fileItem) => {
  if (!fileItem || !fileItem.name) {
    setError('Invalid file for download');
    return false;
  }

  try {
    
    // ✅ Set all progress states properly
    setActiveOperations(prev => ({ ...prev, download: true }));
    setShowDownloadProgress(true);
    setDownloadProgress(0);
    setDownloadMessage('Preparing download...');

    // ✅ Create progress callback that logs and updates state
    const downloadProgressCallback = (progress, message) => {
      
      // Ensure progress is a valid number
      const validProgress = Math.max(0, Math.min(100, Math.round(progress)));
      setDownloadProgress(validProgress);
      setDownloadMessage(message || `Downloading ${fileItem.name}...`);
    };

    // ✅ Call the service with proper progress callback
    const result = await fileService.downloadFile(
      fileItem.path || fileItem.name,
      'files',
      fileItem.size || 0,
      downloadProgressCallback,
      abortControllers.download
    );

    if (result) {
      // ✅ Show completion
      setDownloadProgress(100);
      setDownloadMessage('Download completed!');
      
      // Auto-hide after 2 seconds
      setTimeout(() => {
        setShowDownloadProgress(false);
        setActiveOperations(prev => ({ ...prev, download: false }));
        setDownloadProgress(0);
        setDownloadMessage('');
      }, 2000);
    }

    return result;
  } catch (err) {
    console.error('❌ Download error:', err);
    
    if (err.name === 'AbortError') {
      setDownloadMessage('Download cancelled');
      setDownloadProgress(100);
    } else {
      setError(`Failed to download: ${err.message}`);
      setDownloadMessage(`Failed: ${err.message}`);
      setDownloadProgress(100);
    }

    // Hide progress modal after error
    setTimeout(() => {
      setShowDownloadProgress(false);
      setActiveOperations(prev => ({ ...prev, download: false }));
      setDownloadProgress(0);
      setDownloadMessage('');
    }, 3000);

    return false;
  } finally {
    // Reset abort controller
    abortControllers.download = new AbortController();
  }
}, []);

  // Handle folder download - Updated with progress modal
const downloadFolder = useCallback(async (folderItem) => {
  if (!folderItem || !folderItem.path) {
    setError('Invalid folder for download');
    return false;
  }

  try {
    
    // ✅ Set all progress states properly  
    setActiveOperations(prev => ({ ...prev, download: true }));
    setShowDownloadProgress(true);
    setDownloadProgress(0);
    setDownloadMessage('Preparing folder download...');

    // ✅ Create progress callback that logs and updates state
    const downloadProgressCallback = (progress, message) => {
      
      // Ensure progress is a valid number
      const validProgress = Math.max(0, Math.min(100, Math.round(progress)));
      setDownloadProgress(validProgress);
      setDownloadMessage(message || `Downloading folder...`);
    };

    // ✅ Call the service with proper progress callback
    const result = await fileService.downloadFolder(
      folderItem.path,
      'files',
      downloadProgressCallback,  // This callback MUST be called by the service
      abortControllers.download
    );

    if (result) {
      // ✅ Show completion
      setDownloadProgress(100);
      setDownloadMessage('Folder download completed!');

      // Auto-hide after 2 seconds
      setTimeout(() => {
        setShowDownloadProgress(false);
        setActiveOperations(prev => ({ ...prev, download: false }));
        setDownloadProgress(0);
        setDownloadMessage('');
      }, 2000);
    }

    return result;
  } catch (err) {
    console.error('❌ Folder download error:', err);
    
    if (err.name === 'AbortError') {
      setDownloadMessage('Download cancelled');
      setDownloadProgress(100);
    } else {
      setError(`Failed to download folder: ${err.message}`);
      setDownloadMessage(`Failed: ${err.message}`);
      setDownloadProgress(100);
    }

    // Hide progress modal after error
    setTimeout(() => {
      setShowDownloadProgress(false);
      setActiveOperations(prev => ({ ...prev, download: false }));
      setDownloadProgress(0);
      setDownloadMessage('');
    }, 3000);

    return false;
  } finally {
    // Reset abort controller
    abortControllers.download = new AbortController();
  }
}, []);

  // Handle file deletion
  const deleteFile = useCallback(async (fileItem) => {

    if (!fileItem) {
      setError('Invalid file for deletion');
      return false;
    }

    try {
      setActiveOperations(prev => ({ ...prev, delete: true }));

      // Determine the correct file path to use
      let filePath;

      if (fileItem.path) {
        filePath = fileItem.path;
      } else if (fileItem.fullPath) {
        filePath = fileItem.fullPath;
      } else if (fileItem.webkitRelativePath && fileItem.webkitRelativePath.length > 0) {
        filePath = fileItem.webkitRelativePath;
      } else {
        filePath = fileItem.name;
      }

      await fileService.deleteFile(filePath, 'files');

      setActiveOperations(prev => ({ ...prev, delete: false }));
      return true;
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(`Failed to delete file: ${err.message}`);
      setActiveOperations(prev => ({ ...prev, delete: false }));
      return false;
    }
  }, []);

  // Handle folder deletion
  const deleteFolder = useCallback(async (folderItem) => {
    if (!folderItem || !folderItem.name) {
      setError('Invalid folder for deletion');
      return false;
    }

    try {
      setActiveOperations(prev => ({ ...prev, delete: true }));

      await fileService.deleteFolder(folderItem.name);

      setActiveOperations(prev => ({ ...prev, delete: false }));
      return true;
    } catch (err) {
      console.error('Error deleting folder:', err);
      setError(`Failed to delete folder: ${err.message}`);
      setActiveOperations(prev => ({ ...prev, delete: false }));
      return false;
    }
  }, []);

  // Handle file viewing
  const viewFile = useCallback(async (fileItem) => {
    if (!fileItem || !fileItem.path) {
      setError('Invalid file for viewing');
      return false;
    }

    try {
      setActiveOperations(prev => ({ ...prev, view: true }));

      const result = await fileService.viewFile(fileItem);

      setFileToView(result);

      setActiveOperations(prev => ({ ...prev, view: false }));
      return true;
    } catch (err) {
      console.error('Error viewing file:', err);
      setError(`Failed to view file: ${err.message}`);
      setActiveOperations(prev => ({ ...prev, view: false }));
      return false;
    }
  }, []);

  // Handle folder creation
  const createFolder = useCallback(async (folderName, parentPath) => {
    if (!folderName) {
      setError('Folder name is required');
      return false;
    }

    try {
      const fullPath = parentPath ? `${parentPath}/${folderName}` : folderName;

      await fileService.createFolder(fullPath);

      return true;
    } catch (err) {
      console.error('Error creating folder:', err);
      setError(`Failed to create folder: ${err.message}`);
      return false;
    }
  }, []);

  // Cancel current operation - Updated to handle download progress
  const cancelOperation = useCallback(() => {
  
  if (activeOperations.upload) {
    abortControllers.upload.abort();
    setIsUploadInProgress(false);
    setActiveOperations(prev => ({ ...prev, upload: false }));
  }

  if (activeOperations.download) {
    abortControllers.download.abort();
    setShowDownloadProgress(false);
    setDownloadProgress(0);
    setDownloadMessage('');
    setActiveOperations(prev => ({ ...prev, download: false }));
  }

  // For other operations, simply mark them as inactive
  setActiveOperations({
    upload: false,
    download: false,
    delete: false,
    view: false
  });
}, [activeOperations, setIsUploadInProgress]);

  // Handle download progress modal close
const handleDownloadProgressClose = useCallback(() => {
  
  if (downloadProgress >= 100 || !activeOperations.download) {
    // Download completed or not active - safe to close
    setShowDownloadProgress(false);
    setDownloadProgress(0);
    setDownloadMessage('');
    setActiveOperations(prev => ({ ...prev, download: false }));
  } else {
    // Download still in progress - cancel it
    cancelOperation();
  }
}, [downloadProgress, activeOperations.download, cancelOperation]);

  const renameFile = useCallback(async (fileItem, newName) => {
    if (!fileItem || !newName) {
      setError('Invalid file or name for renaming');
      return false;
    }

    try {
      setActiveOperations(prev => ({ ...prev, rename: true }));

      // Extract the current path components
      const filePath = fileItem.path || fileItem.name;
      const pathParts = filePath.split('/');
      const currentFileName = pathParts.pop(); // Remove current filename
      const folderParent = pathParts.length > 0 ? pathParts.join('/') : '';

      await fileService.renameFile(filePath, newName, folderParent);

      setActiveOperations(prev => ({ ...prev, rename: false }));
      return true;
    } catch (err) {
      console.error('Error renaming file:', err);
      setError(`Failed to rename file: ${err.message}`);
      setActiveOperations(prev => ({ ...prev, rename: false }));
      return false;
    }
  }, []);

  return {
    uploadFiles,
    downloadFile,
    downloadFolder,
    deleteFile,
    deleteFolder,
    viewFile,
    createFolder,
    renameFile,
    cancelOperation,
    activeOperations,
    error,
    clearError,
    fileToView,
    setFileToView,
    // Add download progress state and handlers
    downloadProgress,
    downloadMessage,
    showDownloadProgress,
    handleDownloadProgressClose
  };
};

export default useFileOperations;